import React, { useState } from "react";
import { Box, Button, ButtonBase, Typography } from "@mui/material";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { DiscoveredTool } from "../../api/customMcpApi";
import { categorize } from "./toolCategorize";
import { ToolRow } from "./ToolRow";

interface Props {
  category: string;
  /** Tools visible in this section (already search/filter-narrowed). */
  tools: DiscoveredTool[];
  /** Counts over the WHOLE category, independent of search/filter. */
  enabledCount: number;
  total: number;
  selected: Set<string>;
  onToggle: (upstreamName: string) => void;
  onTagClick: (tag: string) => void;
  onSetMany: (upstreamNames: string[], on: boolean) => void;
  defaultExpanded: boolean;
  /** A search or filter is active: show the matching rows without a click. */
  forceExpanded: boolean;
}

export function ToolCategorySection({
  category,
  tools,
  enabledCount,
  total,
  selected,
  onToggle,
  onTagClick,
  onSetMany,
  defaultExpanded,
  forceExpanded,
}: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const open = forceExpanded || expanded;
  const names = tools.map((t) => t.upstream_name);

  return (
    <Box data-ph-scope="category" sx={{ borderBottom: 1, borderColor: "divider" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
        <ButtonBase
          data-ph={open ? "collapse" : "expand"}
          aria-expanded={open}
          onClick={() => {
            if (!forceExpanded) setExpanded((v) => !v);
          }}
          sx={{ flex: 1, justifyContent: "flex-start", gap: 0.5, py: 0.5, borderRadius: 1 }}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Typography variant="subtitle2" component="span">
            {category} — {enabledCount} of {total} enabled
          </Typography>
        </ButtonBase>
        <Button data-ph="select_all" size="small" aria-label={`Select all ${category}`} onClick={() => onSetMany(names, true)}>
          Select
        </Button>
        <Button data-ph="select_none" size="small" aria-label={`Clear all ${category}`} onClick={() => onSetMany(names, false)}>
          Clear
        </Button>
      </Box>
      {open &&
        tools.map((t) => (
          <ToolRow
            key={t.upstream_name}
            tool={t}
            meta={categorize(t)}
            checked={selected.has(t.upstream_name)}
            onToggle={onToggle}
            onTagClick={onTagClick}
          />
        ))}
    </Box>
  );
}
